import React, { useState, useEffect } from "react";

const TABS = [
  { key: "overview", label: "Overview", emoji: "🧭" },
  { key: "data", label: "Data Behind the Lens", emoji: "📚" },
  { key: "signals", label: "Market Signals", emoji: "📊" },
  { key: "focus", label: "Trade Focus Areas", emoji: "🌐" },
  { key: "rca", label: "Think5Whys Insights", emoji: "🧠" },
  { key: "action", label: "MSME Action View", emoji: "🎯" },
  { key: "about", label: "About & Disclaimer", emoji: "ℹ️" },
];

export default function EximLensHome() {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSector, setSelectedSector] = useState("textiles");

  return (
    <div className="w-full min-h-screen bg-black px-3 py-4 text-gray-100">
      <div className="mb-3 border-b border-blue-500/40 pb-2">
        <h1 className="text-lg lg:text-xl font-semibold text-yellow-200 text-left">
          🚢 EximLens by Think5Whys 🌐
        </h1>
        <p className="text-xs lg:text-sm text-gray-300 text-left">
          Trade intelligence for MSMEs · From data to decision
        </p>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-3 lg:grid-cols-7 gap-1 mb-4 border-b border-gray-700 py-2 place-items-center">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            aria-pressed={activeTab === tab.key}
            className={`flex flex-col items-center justify-center px-1 py-1 h-24 rounded-md text-xs lg:text-sm font-medium
              ${
                activeTab === tab.key
                  ? "bg-teal-700/80 text-yellow-200 shadow-sm"
                  : "bg-gray-800/70 text-gray-300 hover:bg-gray-700 hover:text-gray-100"
              }`}
          >
            <span className="text-lg">{tab.emoji}</span>
            <span className="mt-1 text-xs lg:text-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="bg-black/40 px-4 py-2 min-h-[60vh] text-white [&p]:text-white [&li]:text-white">
        {activeTab === "overview" && <OverviewTab />}
        {activeTab === "data" && <DataSourcesTab />}
        {activeTab === "signals" && <MarketSignalsTab />}
        {activeTab === "focus" && (
          <TradeFocusTab
            selectedSector={selectedSector}
            onSectorChange={setSelectedSector}
          />
        )}
        {activeTab === "rca" && <Think5WhysTab selectedSector={selectedSector} />}
        {activeTab === "action" && <MsmeActionTab selectedSector={selectedSector} />}
        {activeTab === "about" && <AboutTab />}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────

function OverviewTab() {
  return (
    <div className="space-y-4 text-sm [&li]:text-white">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">What is EximLens?</h2>
      <p className="text-sm text-gray-200 leading-relaxed">
        EximLens helps Indian MSMEs interpret official export–import data through a structured <strong>Think5Whys</strong> lens — so you understand not just <em>what</em> is happening, but <em>why</em> it is happening and what you can realistically do about it.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
          <h3 className="font-medium text-teal-300 mb-1.5">Key Features</h3>
          <ul className="list-disc pl-5 space-y-1 [&>li]:text-white text-xs">
            <li>Visual trend analysis (HSN / sector / country)</li>
            <li>Root-cause (5 Whys) interpretation</li>
            <li>MSME-friendly action pathways</li>
            <li>SAKSHAM skill & readiness mapping</li>
          </ul>
        </div>

        <div className="bg-gray-900/50 p-3 rounded border border-gray-700">
          <h3 className="font-medium text-teal-300 mb-1.5">Data Freshness</h3>
          <p className="text-white text-xs">
            Last updated: <strong>December 2025</strong><br />
            Sources: DGCIS, DGFT-NIRYAT, Indian Trade Portal
          </p>
        </div>
      </div>
    </div>
  );
}

function DataSourcesTab() {
  const sources = [
    {
      title: "DGFT – NIRYAT Portal",
      desc: "Latest export/import policy, RoDTEP/ RoSCTL rates, IEC updates, restricted items list, trade notices",
      lastUpdate: "Jan 10, 2026",
    },
    {
      title: "Indian Trade Portal (ITP)",
      desc: "Market access database — tariffs, NTBs, trade statistics, export potential maps, buyer requirements",
      lastUpdate: "Dec 28, 2025",
    },
    {
      title: "DGCIS Trade Statistics",
      desc: "Monthly & annual HSN 8-digit level export/import values, quantity, growth rates, top partners",
      lastUpdate: "Nov 2025 (Apr–Oct 2025 data)",
    },
  ];

  return (
    <div className="space-y-2">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">Official Data Sources</h2>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sources.map((item, i) => (
          <div
            key={i}
            className="bg-gray-900/60 p-4 rounded-lg border border-gray-700 hover:border-teal-700/50 transition-colors"
          >
            <h3 className="font-medium text-sm lg:text-lg text-teal-300">{item.title}</h3>
            <p className="text-xs text-gray-300 mt-2 leading-relaxed">{item.desc}</p>
            <p className="text-xs text-gray-500 mt-3">
              Last refreshed: <span className="text-gray-400">{item.lastUpdate}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketSignalsTab() {
  const dummySignals = [
    { label: "Textiles & Apparel", delta: "+18%", color: "text-green-400" },
    { label: "Engineering Goods", delta: "+7%", color: "text-green-300" },
    { label: "Gems & Jewellery", delta: "-11%", color: "text-red-400" },
    { label: "Electronics", delta: "+34%", color: "text-green-400" },
    { label: "Marine Products", delta: "-4%", color: "text-orange-300" },
    { label: "Chemicals", delta: "+9%", color: "text-green-300" },
  ];

  return (
    <div className="space-y-5">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">Market Signals (Apr–Oct 2025 YoY)</h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {dummySignals.map((sig, i) => (
          <div
            key={i}
            className="bg-gray-900/60 p-3 rounded-lg border border-gray-700 text-center"
          >
            <div className="text-sm font-medium text-gray-200">{sig.label}</div>
            <div className={`text-xl font-bold mt-1 ${sig.color}`}>{sig.delta}</div>
          </div>
        ))}
      </div>

      <div className="bg-gray-950/70 p-4 rounded border border-gray-800 text-center text-sm text-gray-400 italic">
        Interactive charts coming soon (HSN-level treemap, country radar, time-series)
      </div>
    </div>
  );
}

const SECTORS = ["textiles", "engineering", "gems", "electronics", "marine", "chemicals"];

function TradeFocusTab({ selectedSector, onSectorChange }) {
  const dummyProducts = {
    textiles: ["Cotton yarn", "Readymade garments", "Home textiles", "Man-made fibre apparel"],
    engineering: ["Auto components", "Hand tools", "Pumps", "Transformers"],
    gems: ["Cut & polished diamonds", "Gold jewellery", "Silver jewellery"],
    electronics: ["Mobile phones", "Charger & adapters", "PCB assemblies"],
    marine: ["Frozen shrimp", "Fish fillets", "Surimi"],
    chemicals: ["Dyes & pigments", "Agro chemicals", "Pharma APIs"],
  };

  return (
    <div className="space-y-5">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">Trade Focus Areas</h2>

      <div className="grid grid-cols-5 lg:grid-cols-8 gap-2">
        {SECTORS.map((s) => (
          <button
            key={s}
            onClick={() => onSectorChange(s)}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition ${
              selectedSector === s
                ? "bg-teal-700 text-yellow-100"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-gray-900/60 p-4 rounded border border-gray-700">
        <h3 className="font-medium text-teal-300 mb-3">
          Top products in <span className="capitalize">{selectedSector}</span>
        </h3>
        <ul className="space-y-2 text-sm text-gray-200">
          {dummyProducts[selectedSector].map((prod, i) => (
            <li key={i} className="flex justify-between text-white">
              <span>{prod}</span>
              <span className="text-green-300">↑ {12 + i * 3}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Think5WhysTab({ selectedSector }) {
  const dummy5whys = {
    textiles: [
      "Export growth slowed in Q3 2025",
      "EU buyers shifted to Bangladesh & Vietnam",
      "Price competitiveness eroded due to high power & yarn costs",
      "Many units still use old shuttle looms & outdated dyeing machines",
      "Upgrade to air-jet / rapier + invest in solar + effluent treatment",
    ],
    engineering: [
      "Engineering exports grew only 7%",
      "US & EU demand for machinery subdued",
      "Lack of after-sales service network abroad",
      "Weak brand visibility & no local warehousing",
      "Join export consortia + invest in overseas demo centres",
    ],
  };

  const whys = dummy5whys[selectedSector] || dummy5whys.textiles;

  return (
    <div className="space-y-5">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">Think5Whys – Root Cause Analysis</h2>
      <p className="text-sm text-gray-300 capitalize">
        Selected sector: <strong>{selectedSector}</strong>
      </p>

      <ol className="space-y-4 pl-6 list-decimal text-sm">
        {whys.map((text, i) => (
          <li key={i} className={i === 4 ? "font-medium text-teal-300" : "text-gray-200"}>
            {text}
          </li>
        ))}
      </ol>
    </div>
  );
}

function MsmeActionTab({ selectedSector }) {
  const dummyActions = {
    textiles: {
      readiness: "Medium",
      effort: "8–14 months",
      priority: "High",
      actions: [
        "Install energy-efficient motors & LED lighting",
        "Adopt zero-liquid-discharge (ZLD) system",
        "Upgrade at least 40% looms to shuttle-less",
        "Get GOTS / OEKO-TEX certification",
      ],
    },
    engineering: {
      readiness: "Low–Medium",
      effort: "12–24 months",
      priority: "Medium",
      actions: [
        "Develop 3–5 key export-ready SKUs",
        "Create basic product catalogue in English",
        "Partner with EPC / trade body for trade fair participation",
        "Start basic CRM & after-sales tracking",
      ],
    },
  };

  const action = dummyActions[selectedSector] || dummyActions.textiles;

  return (
    <div className="space-y-5">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">MSME Action View</h2>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-gray-900/60 p-3 rounded border border-gray-700 text-center">
          <div className="text-xs text-gray-400">Readiness Level</div>
          <div className="text-xl font-bold text-teal-300 mt-1">{action.readiness}</div>
        </div>
        <div className="bg-gray-900/60 p-3 rounded border border-gray-700 text-center">
          <div className="text-xs text-gray-400">Est. Effort</div>
          <div className="text-xl font-bold text-yellow-200 mt-1">{action.effort}</div>
        </div>
        <div className="bg-gray-900/60 p-3 rounded border border-gray-700 text-center">
          <div className="text-xs text-gray-400">Priority</div>
          <div className="text-xl font-bold text-orange-300 mt-1">{action.priority}</div>
        </div>
      </div>

      <div>
        <h3 className="font-medium text-teal-300 mb-2">Recommended Next Steps</h3>
        <ul className="space-y-2 text-sm text-gray-200 ">
          {action.actions.map((act, i) => (
            <li key={i} className="flex items-start gap- text-white">
              <span className="text-green-400 mt-1">✔</span>
              <span>{act}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function AboutTab() {
  return (
    <div className="space-y-4 text-sm">
      <h2 className="text-sm lg:text-lg font-medium text-yellow-200">About & Disclaimer</h2>

      <p className="text-gray-200 leading-relaxed">
        EximLens is developed as part of the <strong>SAKSHAM MSME Program</strong> by Think5Whys
        Transformation-as-a-Platform. It aims to make official trade data actionable for micro,
        small and medium enterprises.
      </p>

      <div className="bg-red-950/30 border border-red-800/40 p-4 rounded text-xs text-gray-200">
        <strong className="text-orange-300 block mb-2">Important Disclaimer</strong>
        <p className="text-white">
          This platform provides interpretative insights and visual summaries based solely on
          publicly available government and trade portal data. It is <strong>not</strong> a
          substitute for professional financial, legal, taxation or export-import advisory
          services. All decisions should be independently verified and validated by the user
          with qualified experts. Think5Whys / SAKSHAM accepts no liability for any loss or
          damage arising from the use of this tool.
        </p>
      </div>
    </div>
  );
}